import { motion } from "framer-motion";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface HistoricalEvent {
  title: string;
  description: string;
  category?: string;
}

interface EventTimelineProps {
  year: number;
  events: HistoricalEvent[];
}

export default function EventTimeline({ year, events }: EventTimelineProps) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 },
    },
  };

  return (
    <motion.div
      className="w-full max-w-2xl mx-auto space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="text-center space-y-2 mb-8">
        <h2 className="text-4xl font-bold tracking-tight">{year}</h2>
        <p className="text-muted-foreground">
          {events.length === 0
            ? "No events recorded for this year"
            : `${events.length} ${events.length === 1 ? "event" : "events"} found`}
        </p>
      </div>

      {events.length > 0 && (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary/50 to-primary/10" />

          {/* Events */}
          <div className="space-y-6">
            {events.map((event, idx) => (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="relative pl-24"
              >
                {/* Timeline dot */}
                <div className="absolute left-2 top-2 w-12 h-12 rounded-full border-4 border-background bg-primary flex items-center justify-center">
                  <div className="w-4 h-4 rounded-full bg-background" />
                </div>

                <Card className="hover:border-primary/50 transition-colors">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-semibold leading-tight text-lg">
                        {event.title}
                      </h3>
                      {event.category && (
                        <Badge variant="secondary" className="shrink-0">
                          {event.category}
                        </Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {event.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      )}
    </motion.div>
  );
}
